import { Avatar, Box, Divider, Drawer, IconButton, Typography, Popover, Tooltip } from '@mui/material';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import '../../../assets/style/sass/custom.scss';
import Logo from '../../../assets/images/brand/dosen/logo.png';
import { useMemo, useState, useEffect } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArticleIcon from '@mui/icons-material/Article';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { IRoleObj, groupRoleListUpdate } from '../../../redux/slices/groupRolesListSlice';
import { updateSelectedId } from '../../../redux/slices/selectedGroupSlice';
import { getUserDetails } from '../../../utils/orgName';
import { breadCrumbsActions } from '../../../redux/slices/breadcrumbs/breadcrumbsSlice';
import { saveAnswerActions } from '../../../redux/slices/saveAnswersSlice';
import { setSelectedValue } from '../../../redux/slices/selectedValueSlice';
import { fetchMentorTasks } from '../../../redux/slices/mentorTasksSlice';
import { API } from '../../../api';
import { userInfoUpdate } from '../../../redux/slices/user-profile/userProfieSlice';
import { toast } from 'react-toastify';
import _ from 'lodash';
import * as amplitude from '@amplitude/analytics-browser';
import { groupLoaderActions } from '../../../redux/slices/groupLoaderSlice';
import { AGILE_ORG } from '../../../utils/constants';
import useProfileDetails from '../../../hooks/useProfileDetails';
import { useLogout } from '../../../hooks';
import Intercom from '@intercom/messenger-js-sdk';
import { useModule } from '../../../utils/useModule';

interface TabWithLockIconProps {
  tabName: string;
  isActive: boolean;
  onClick: () => void;
  tabDisable?: boolean;
  lockTooltip?: string;
}

interface NavItem {
  label: string;
  icon?: React.ReactNode;
  route?: string;
  disabled?: boolean;
  lockTooltip?: string;
}

/* ===========================================================
   HEADER TITLE DINÂMICO PELO PATH
   =========================================================== */
function useDynamicHeaderTitle() {
  const location = useLocation();
  const selectedGroupName = useAppSelector((state) => state.selectedGroup.data?.name);

  return useMemo(() => {
    if (location.pathname.includes('/overview')) return 'PROGRAMS DASHBOARD';
    if (location.pathname.includes('/users')) return 'MEMBERS';
    if (location.pathname.includes('/settings')) return 'PROGRAM MANAGEMENT';
    if (location.pathname.includes('/insights')) return 'INSIGHTS';
    if (location.pathname.includes('/matches')) return 'MATCHES';
    if (location.pathname.includes('/calendar')) return 'CALENDAR';
    if (location.pathname.includes('/profile')) return 'PROFILE';
    if (location.pathname.includes('/library')) return 'RESOURCES';
    if (location.pathname.includes('/community')) return 'NETWORK';
    if (location.pathname.includes('/new-program/start')) return 'START NEW PROGRAM';
    if (location.pathname.includes('/new-program/masterclass/step-2')) return 'CREATE NEW MASTERCLASS';
    if (location.pathname.includes('/new-program/masterclass/manual')) return 'CREATE NEW MASTERCLASS';
    if (location.pathname.includes('/new-program/mentorship')) return 'CREATE NEW MENTORSHIP';
    if (location.pathname.includes('/new-program/guided-masterclass/manual')) return 'CREATE NEW GUIDED MASTERCLASS';

    if (location.pathname.includes('/home/mentee/level')) {
      return selectedGroupName || 'PROGRAM PORTFOLIO';
    }
    if (location.pathname.includes('/chat')) return 'CHAT';
    if (location.pathname.includes('/home/user')) return 'PROGRAM PORTFOLIO';
    if (location.pathname.includes('/ai')) return 'BUILD WITH ELLIOTT';

    return 'Elliott';
  }, [location.pathname, selectedGroupName]);
}

function RoleSwitch({
  isAdmin,
  disabled,
  allowAdmin,
  allowUser,
  onChange
}: {
  isAdmin: boolean;
  disabled: boolean;
  allowAdmin: boolean;
  allowUser: boolean;
  onChange: (target: 'admin' | 'user') => void;
}) {
  const labelStyles = (active: boolean) => ({
    color: active ? '#FDFDFD' : '#868686',
    fontSize: 12,
    fontWeight: 500,
    fontFamily: 'Inter, sans-serif',
    cursor: disabled ? 'not-allowed' : 'pointer'
  });

  const handleToggle = (target: 'admin' | 'user') => {
    if (disabled) return;
    if (target === 'admin' && !allowAdmin) return;
    if (target === 'user' && !allowUser) return;
    onChange(target);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography sx={labelStyles(!isAdmin)} onClick={() => handleToggle('user')}>
        User
      </Typography>
      <Box
        onClick={() => handleToggle(isAdmin ? 'user' : 'admin')}
        sx={{
          width: 32,
          height: 16,
          px: 0.5,
          background: '#2A2A2A',
          borderRadius: 79,
          outline: '1px solid #444444',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isAdmin ? 'flex-end' : 'flex-start',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1
        }}
      >
        <Box sx={{ width: 12, height: 12, background: '#868686', borderRadius: '999px' }} />
      </Box>
      <Typography sx={labelStyles(isAdmin)} onClick={() => handleToggle('admin')}>
        Admin
      </Typography>
    </Box>
  );
}

/* ===========================================================
   DRAWER LATERAL — IGUAL AO FIGMA + NAVEGAÇÃO NOVA
   =========================================================== */
function ProgramSideDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeGroupId = '' } = useParams<{ id: string }>();

  const grpRoleListState = useAppSelector((state) => state.groupRoleList);
  const groupRoles = grpRoleListState.data || [];
  const selectedGroup = useAppSelector((state) => state.selectedGroup.data);
  const userProfileData = useAppSelector((state) => state.userProfile.data);
  const oid = useAppSelector((state) => state.orgDetails.data?.oid);
  const [roleSwitching, setRoleSwitching] = useState(false);

  const { orgId, coId } = getUserDetails();
  const { headshot, firstName, lastName } = useProfileDetails();
  const { doLogout } = useLogout();

  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userMenuAnchor);

  /* ===========================================================
     Funções internas originais (não mexido)
     =========================================================== */

  const changeRoute = (roleObj: IRoleObj) => {
    dispatch(updateSelectedId(roleObj));
    if (location.pathname === '/app/profile/assessment' && roleObj.role === 'mentor') {
      navigate(`/app/profile/assessment`);
    } else {
      navigate(`/app/home/${roleObj.role}`);
    }
  };

  const dispatchBreadCrumbs = (id?: string) => {
    const grpId = id ? id : 'admin';
    dispatch(
      breadCrumbsActions.removeBreadCrumbs({
        id: grpId,
        name: 'Program',
        url: `/app/programdetails/${orgId}/${coId}/${grpId}/overview`
      })
    );
    dispatch(groupLoaderActions.updateState(true));
    navigate(`/app/programdetails/${orgId}/${coId}/${grpId}/overview`);
  };

  const redirectAdmin = (selectedObj: IRoleObj) => {
    dispatchBreadCrumbs(selectedObj?.id);
  };

  const switchToUser = async () => {
    const userTarget = getUserRoleTarget(); // já existe na sua lógica

    if (!userTarget) {
      navigate('/app/home/user');
      return;
    }

    setRoleSwitching(true);

    try {
      // 1 — atualizar lista de roles
      dispatch(groupRoleListUpdate.upadateState({ ...grpRoleListState, loader2: true }));
      const response = await API.groupsListForUser(orgId);

      // 2 — atualizar lastseen no backend
      try {
        const updateLastSeenResponse = await API.updateLastseen(userTarget);
        if (updateLastSeenResponse.status === 200) {
          dispatch(userInfoUpdate({ ...userProfileData, lastseen: userTarget }));
        }
      } catch (e) {
        toast.error(_.get(e, 'response.data.message', `couldn't update last seen`));
      }

      // 3 — atualizar redux group roles
      dispatch(
        groupRoleListUpdate.upadateState({
          loading: false,
          data: response?.data?.data || [],
          error: false,
          errroText: '',
          loader2: false
        })
      );

      // 4 — atualizar selectedId Redux
      dispatch(updateSelectedId({ ...userTarget, active: true }));

      // 5 — atualizar breadcrumbs
      dispatch(
        breadCrumbsActions.removeBreadCrumbs({
          id: userTarget.id,
          name: 'Program',
          url: `/app/programdetails/${orgId}/${coId}/${userTarget.id}/overview`
        })
      );

      // 6 — navegar
      navigate('/app/home/user');
    } catch (e) {
      console.error(e);
    } finally {
      setRoleSwitching(false);
    }
  };

  const updateLastSeenAndRoles = async (selectedObj: IRoleObj) => {
    try {
      dispatch(groupRoleListUpdate.upadateState({ ...grpRoleListState, loader2: true }));
      const response = await API.groupsListForUser(orgId);
      if (response.status === 200) {
        try {
          const updateLastSeenResponse = await API.updateLastseen(selectedObj);
          if (updateLastSeenResponse.status === 200) {
            dispatch(userInfoUpdate({ ...userProfileData, lastseen: selectedObj }));
          }
        } catch (e) {
          toast.error(_.get(e, 'response.data.message', `couldn't update last seen`));
        }

        dispatch(
          groupRoleListUpdate.upadateState({
            loading: false,
            data: response.data.data || [],
            error: false,
            errroText: '',
            loader2: false
          })
        );
      }
    } catch (e) {
      dispatch(
        groupRoleListUpdate.upadateState({
          loading: false,
          data: undefined,
          error: true,
          errroText: _.get(e, 'response.data.message', `Failed to get user group role list`),
          loader2: false
        })
      );
    }
  };

  /* ===========================================================
     SWITCH DE ROLE — original
     =========================================================== */

  const allowedUserRoles = groupRoles.filter((each) => {
    if (each.role === 'admin') return false;
    if (each.role === 'mentee' && oid === AGILE_ORG) return false;
    return true;
  });

  const getUserRoleTarget = (): IRoleObj | undefined => {
    const selectedMatch = allowedUserRoles.find(
      (each) => each.id === selectedGroup.id && each.role === selectedGroup.role
    );

    const lastSeenFromStorage = (() => {
      try {
        return JSON.parse(localStorage.getItem('lastseen') || '');
      } catch (error) {
        return undefined;
      }
    })();

    const lastSeenMatch = lastSeenFromStorage
      ? allowedUserRoles.find((each) => each.id === lastSeenFromStorage.id && each.role === lastSeenFromStorage.role)
      : undefined;

    const profileLastSeen = userProfileData?.lastseen;
    const profileMatch = profileLastSeen
      ? allowedUserRoles.find((each) => each.id === profileLastSeen.id && each.role === profileLastSeen.role)
      : undefined;

    return selectedMatch || lastSeenMatch || profileMatch || allowedUserRoles[0];
  };

  const adminRole = groupRoles.find((each) => each.role === 'admin');
  const hasUserRole = allowedUserRoles.length > 0;
  const isAdminSelected = selectedGroup.role === 'admin' || routeGroupId === 'admin';

  const adminNavItems: NavItem[] = [
    {
      label: 'Programs Dashboard',
      icon: <AutoAwesomeIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />,
      route: `/app/programdetails/${orgId}/${coId}/admin/overview`
    },
    {
      label: 'Resources',
      // icon: <ArticleIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />,
      route: '/app/library'
    },
    {
      label: 'Insights',
      icon: <LockOutlinedIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />,
      route: '/app/insights',
      lockTooltip: "We're improving the metrics display to bring you better program intelligence soon.",
      disabled: true
    },
    { label: 'Calendar', route: '/app/calendar' },
    { label: 'Settings', route: '/app/profile' }
  ];

  const userNavItems: NavItem[] = [
    {
      label: 'Program Portfolio',
      icon: '',
      route: '/app/home/user'
    },
    { label: 'Resources', route: '/app/library', disabled: true },
    { label: 'Chat', route: '/app/chat' },
    { label: 'Calendar', route: '/app/calendar' },
    { label: 'Network', route: '/app/community' }
  ];

  const navItems = isAdminSelected ? adminNavItems : userNavItems;
  const [localRole, setLocalRole] = useState<'admin' | 'user'>(isAdminSelected ? 'admin' : 'user');

  useEffect(() => {
    setLocalRole(isAdminSelected ? 'admin' : 'user');
  }, [isAdminSelected]);

  const handleRoleToggle = (target: 'admin' | 'user') => {
    if (roleSwitching) return;
    setLocalRole(target);
    setRoleSwitching(true);
    onClose(); // close drawer immediately for a smoother transition

    const performSwitch = async () => {
      try {
        if (target === 'user') {
          await switchToUser();
          return;
        }

        const nextRole = adminRole || { id: 'admin', name: 'Admin', role: 'admin', active: true };
        await updateLastSeenAndRoles(nextRole);
        dispatch(updateSelectedId({ ...nextRole, active: true }));
        redirectAdmin(nextRole);
      } finally {
        setRoleSwitching(false);
      }
    };

    requestAnimationFrame(() => {
      performSwitch();
    });
  };

  useEffect(() => {
    if (routeGroupId === 'admin' && selectedGroup.role !== 'admin') {
      const adminSelection = adminRole || { id: 'admin', name: 'Admin', role: 'admin', active: true };
      dispatch(updateSelectedId({ ...adminSelection, active: true }));
    }
  }, [routeGroupId, adminRole, dispatch, selectedGroup.role]);

  /* ===========================================================
     RENDER DO DRAWER
     =========================================================== */

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="left"
      PaperProps={{
        sx: {
          width: 300,
          bgcolor: '#0F0F0F',
          borderRight: '1px solid #2A2A2A',
          p: 1.25,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          p: 1.25,
          background: '#0F0F0F',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5
        }}
      >
        {/* Header + Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box sx={{ py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconButton onClick={onClose} size="small">
              <CloseIcon sx={{ color: 'white' }} fontSize="large" />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RoleSwitch
              isAdmin={localRole === 'admin'}
              disabled={roleSwitching}
              allowAdmin={Boolean(adminRole)}
              allowUser={hasUserRole}
              onChange={handleRoleToggle}
            />
          </Box>
        </Box>

        {/* Menu */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', pr: 0.5 }}>
          {navItems.map((item, idx) => {
            const content = (
              <Box
                sx={{
                  height: 44,
                  py: 1.1,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  '&:hover': item.disabled ? {} : { background: '#1A1A1A' }
                }}
                onClick={() => {
                  if (item.route && !item.disabled) {
                    navigate(item.route);
                    onClose();
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  {item.icon ? (
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {item.icon}
                    </Box>
                  ) : null}
                  <Typography
                    sx={{
                      color: '#FDFDFD',
                      fontSize: 16,
                      fontWeight: 400,
                      textTransform: 'uppercase',
                      fontFamily: 'Inter, sans-serif'
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              </Box>
            );

            return (
              <Box key={item.label}>
                <Tooltip title={item.lockTooltip || ''} disableHoverListener={!item.lockTooltip} arrow>
                  <span style={{ width: '100%', display: 'block' }}>{content}</span>
                </Tooltip>
                {idx === 0 || idx === 2 || idx === 3 ? <Divider sx={{ my: 1, borderColor: '#444444' }} /> : null}
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 'auto' }}>
          <Box
            sx={{
              width: '100%',
              p: 1.25,
              borderRadius: 0.8,
              outline: '1px solid #2A2A2A',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                src={headshot}
                sx={{ width: 40, height: 40, borderRadius: '999px', background: '#1C1C1C', cursor: 'pointer' }}
                onClick={() => {
                  navigate('/app/profile');
                }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                  {firstName} {lastName}
                </Typography>
                <Typography sx={{ color: '#444444', fontSize: 12, fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>
                  User
                </Typography>
              </Box>
              <Box>
                <IconButton
                  size="small"
                  onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                  sx={{
                    color: '#FDFDFD',
                    width: 28,
                    height: 28
                  }}
                >
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
                <Popover
                  open={userMenuOpen}
                  anchorEl={userMenuAnchor}
                  onClose={() => setUserMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: {
                      bgcolor: '#0F0F0F',
                      border: '1px solid #2A2A2A',
                      mt: 1
                    }
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#1C1C1C' }
                    }}
                    onClick={() => {
                      doLogout();
                      setUserMenuAnchor(null);
                    }}
                  >
                    <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>Logout</Typography>
                  </Box>
                </Popover>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}

/* ===========================================================
   HEADER PRINCIPAL (FIGMA)
   =========================================================== */
function ProgramHeader() {
  const title = useDynamicHeaderTitle();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box
        sx={{
          height: 86,
          borderBottom: '1px solid #2A2A2A',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: '#0F0F0F'
        }}
      >
        <IconButton onClick={() => setOpen(true)}>
          <MenuIcon sx={{ color: '#FDFDFD' }} />
        </IconButton>

        <Box className="flex-1 flex items-center justify-center gap-2">
          <span className="text-white text-[20px] uppercase font-normal leading-[22px]">{title}</span>
        </Box>

        <img src={Logo} alt="Dosen Logo" style={{ width: 92, height: 18, objectFit: 'contain' }} />
      </Box>

      <ProgramSideDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/* ===========================================================
   WRAPPER — FUNDO PRETO + SCROLL INTERNO
   =========================================================== */
function ProgramDetailsWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#0F0F0F',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <ProgramHeader />

      {/* Conteúdo com scroll */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', padding: '25px' }}>{children}</Box>
    </Box>
  );
}

const Layout = () => {
  // Intercom initialization
  const { firstName, lastName, email } = useProfileDetails();
  const selectedGroup = useAppSelector((state) => state.selectedGroup.data);
  const groupRole = selectedGroup.role || '';
  const module = useModule();
  const programType = module.isMentorship ? 'mentorship' : module.isMasterclass ? 'masterclass' : 'hybrid';
  const intercomAppId = process.env.REACT_APP_INTERCOMM_APP_ID;

  useEffect(() => {
    const initializeIntercom = async () => {
      const username = `${firstName || ''} ${lastName || ''}`.trim() || 'Unknown';

      if (intercomAppId && username && groupRole && programType) {
        try {
          const response = await API.intercomToken();
          if (response?.status === 200 && response?.data) {
            Intercom({
              app_id: intercomAppId,
              intercom_user_jwt: response.data,
              name: username,
              'User Role': groupRole,
              'Program Module': programType,
              session_duration: 86400000 // 1 day
            });
          }
        } catch (error) {
          console.warn('Failed to initialize Intercom:', error);
        }
      }
    };

    initializeIntercom();
  }, [firstName, lastName, email, intercomAppId, groupRole, programType]);

  return (
    <Box className="w-full h-full p-0" sx={{ height: '100% !important' }}>
      <ProgramDetailsWrapper>
        <Outlet />
      </ProgramDetailsWrapper>
    </Box>
  );
};

export default Layout;
